import { useEffect, useState } from "react";
import { useApp } from "./context";
import { usePlayer } from "./player";
import { Icon, Modal } from "./visual";
import {
  isLocalMedia,
  requestWirelessPlayback,
  wirelessSupport,
  WirelessMedia,
} from "./wireless";

export default function Devices() {
  const p = usePlayer(),
    { t } = useApp();
  const audio = p.audio.current;
  const [support, setSupport] = useState(() => wirelessSupport(audio));
  const [error, setError] = useState("");
  const [available, setAvailable] = useState<boolean | null>(null);
  const [connecting, setConnecting] = useState(false);
  const local = isLocalMedia(
    audio?.currentSrc || audio?.src || "",
    location.href,
  );
  useEffect(() => {
    if (!audio) return;
    const remote = audio.remote;
    const update = () => {
      setSupport(wirelessSupport(audio));
      setConnecting(remote?.state === "connecting");
    };
    const availability = (event: Event) =>
      setAvailable(
        (event as Event & { availability: string }).availability ===
          "available",
      );
    audio.addEventListener(
      "webkitcurrentplaybacktargetiswirelesschanged",
      update,
    );
    audio.addEventListener(
      "webkitplaybacktargetavailabilitychanged",
      availability,
    );
    for (const event of ["connect", "disconnect", "connecting"])
      remote?.addEventListener(event, update);
    let disposed = false,
      watchId: number | undefined;
    if (remote?.watchAvailability && !local) {
      remote
        .watchAvailability(setAvailable)
        .then((id) => {
          if (disposed) void remote.cancelWatchAvailability(id).catch(() => {});
          else watchId = id;
        })
        .catch(() => {});
    }
    update();
    return () => {
      disposed = true;
      audio.removeEventListener(
        "webkitcurrentplaybacktargetiswirelesschanged",
        update,
      );
      audio.removeEventListener(
        "webkitplaybacktargetavailabilitychanged",
        availability,
      );
      for (const event of ["connect", "disconnect", "connecting"])
        remote?.removeEventListener(event, update);
      if (watchId !== undefined)
        void remote.cancelWatchAvailability(watchId).catch(() => {});
    };
  }, [audio, local]);
  async function choose(kind: "airplay" | "remote") {
    if (!audio) return;
    setError("");
    try {
      await requestWirelessPlayback(audio as WirelessMedia, kind);
      setSupport(wirelessSupport(audio));
    } catch (e) {
      if (
        (e as Error).name !== "NotAllowedError" &&
        (e as Error).name !== "AbortError"
      )
        setError(t("deviceFailed"));
    }
  }
  return (
    <Modal title={t("listenDevices")} onClose={() => p.setDevicesOpen(false)}>
      <div className="listening-devices">
        <p className="device-state" role="status">
          <Icon name={support.connected ? "cast" : "headphones"} />
          {t(
            support.connected
              ? "wirelessConnected"
              : connecting
                ? "deviceConnecting"
                : "thisDevice",
          )}
        </p>
        <section>
          <h3>
            <Icon name="airplay" size={24} /> AirPlay
          </h3>
          <p>{t(support.airplay ? "airplayHint" : "airplaySafari")}</p>
          <button
            className="button"
            disabled={!support.airplay || !p.episode}
            onClick={() => void choose("airplay")}
          >
            {t("chooseAirplay")}
          </button>
        </section>
        <section>
          <h3>
            <Icon name="cast" size={24} /> Chromecast
          </h3>
          <p>{t(local ? "castLocal" : "castBrowser")}</p>
          {support.remote && !support.airplay && !local && (
            <button
              className="button"
              disabled={!p.episode || connecting}
              onClick={() => void choose("remote")}
            >
              {t("chooseDevice")}
            </button>
          )}
          {available === false && !local && <p>{t("noDevices")}</p>}
          <details className="cast-guide" open={local || !support.remote}>
            <summary>{t("castTab")}</summary>
            <ol>
              <li>{t("castStepOne")}</li>
              <li>{t("castStepTwo")}</li>
              <li>{t("castStepThree")}</li>
            </ol>
            <a
              href="https://support.google.com/chromecast/answer/3228332"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("castHelp")} <Icon name="arrow" size={18} />
            </a>
          </details>
        </section>
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
